import { Directive, inject, input, OnInit, output, TemplateRef, ViewContainerRef } from '@angular/core';
import { FormWizardComponent } from './form-wizard.component';

@Directive({
  selector: '[workUiFormWizardStep]',
})
export class FormWizardStepDirective implements OnInit {
  template = inject<TemplateRef<any>>(TemplateRef);
  public stepTitle = input<string>();
  public stepButtonNextText = input<string>();
  public stepHidden = input<boolean>();
  public stepActive = output<boolean>();
  public formControlNames: string[] = [];
  private viewContainerRef = inject(ViewContainerRef);
  private wizard = inject(FormWizardComponent, { optional: true, host: true });

  ngOnInit() {
    this.wizard?.registerStep(this);
    this.setFormControlNames();
  }

  /**
   * Creates an embedded view of the template because you cannot access it via this.template since it is a ng-template.
   * The just iterating over all elements looking for [formControlName] attributes.
   *
   * @private
   */
  private setFormControlNames() {
    // Create an embedded view of the template
    const embeddedView = this.viewContainerRef.createEmbeddedView(this.template);

    // Access the root node of the view's DOM
    const rootNodes: HTMLElement[] = embeddedView.rootNodes.filter((node) => node instanceof HTMLElement);

    // Attribute names that reference a form control in the wizard's FormGroup.
    // Besides the standard `formControlName`, the work-ui datepicker exposes its
    // controls via custom inputs (single date / date range), which must also be
    // picked up so that step validation includes them.
    const formControlAttributeNames = [
      'formControlName',
      'singleDateFormControlName',
      'rangeStartDateFormControlName',
      'rangeEndDateFormControlName',
    ];

    // Recursively search all nodes to find elements referencing a form control
    const formControlNames: string[] = [];
    const findFormControls = (nodes: HTMLElement[]) => {
      nodes.forEach((node) => {
        formControlAttributeNames.forEach((attributeName) => {
          if (node.hasAttribute(attributeName)) {
            const formControlName = node.getAttribute(attributeName);
            if (formControlName) {
              formControlNames.push(formControlName);
            }
          }
        });

        // Check for child nodes
        if (node.children.length) {
          findFormControls(Array.from(node.children) as HTMLElement[]);
        }
      });
    };

    findFormControls(rootNodes);

    // Destroy the created view to avoid rendering side effects
    embeddedView.destroy();

    this.formControlNames = formControlNames;
  }
}
